import { useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { isRealApt } from "cadendar-shared";
import { trpc } from "../../main/components/trpc.ts";
import useUser from "../../users/hooks/useUser.ts";
import SavedPayslipsPanel from "./SavedPayslipsPanel.tsx";
import { getSpecialLeaveReasonsForDownload } from "../specialLeaveReasons.ts";

function sanitizeDecimalInput(value: string) {
  return value.replace(/[^0-9,\.]/g, "");
}

function normalizeDecimal(value: string) {
  return value.trim().replace(",", ".");
}

function parsePositiveDecimal(value: string) {
  const normalized = normalizeDecimal(value);
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function parseNonNegativeDecimal(value: string) {
  const normalized = normalizeDecimal(value);
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return parsed;
}

function parsePercent(value: string) {
  const parsed = parseNonNegativeDecimal(value);
  if (parsed === null || parsed > 100) {
    return null;
  }
  return parsed;
}

function formatAmount(value: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

function formatDecimal(value: string | number) {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed)
    ? parsed.toFixed(2).replace(".", ",")
    : String(value);
}

function formatRate(value: string | number) {
  return `${formatDecimal(value)}%`;
}

function formatDayCount(value: number) {
  const formattedValue = Number.isInteger(value)
    ? String(value)
    : value.toFixed(1).replace(".", ",");

  return `${formattedValue} ${value === 1 ? "jour ouvrable" : "jours ouvrables"}`;
}

function formatSpecialLeaveDayCount(value: number) {
  const formattedValue = Number.isInteger(value)
    ? String(value)
    : value.toFixed(1).replace(".", ",");

  return `${formattedValue} ${value === 1 ? "jour programmé" : "jours programmés"}`;
}

function formatHourCount(value: string | number) {
  const parsed = typeof value === "number" ? value : Number(value);
  const formattedValue = Number.isInteger(parsed)
    ? String(parsed)
    : parsed.toFixed(2).replace(".", ",");

  return `${formattedValue} ${parsed === 1 ? "heure" : "heures"}`;
}

function formatPercentFromRatio(value: string) {
  return `${(Number(value) * 100).toFixed(2).replace(".", ",")}%`;
}

function formatPaidLeaveRange({
  range,
  periodDate,
}: {
  range: {
    startDate: string;
    endDate: string;
    returnDate: string;
    countedDays: number;
  };
  periodDate: DateTime;
}) {
  const start = formatDate(range.startDate);
  const end = formatDate(range.endDate);
  const parsedReturnDate = DateTime.fromISO(range.returnDate);
  const returnDate = formatDate(range.returnDate);
  const countedDays = formatDayCount(range.countedDays);
  const shouldDisplayReturnDate = parsedReturnDate.hasSame(periodDate, "month");

  if (!shouldDisplayReturnDate) {
    if (range.startDate === range.endDate) {
      return `${start} (${countedDays})`;
    }

    return `${start} au ${end} (${countedDays})`;
  }

  if (range.startDate === range.endDate) {
    return `${start} (reprise ${returnDate}, ${countedDays})`;
  }

  return `${start} au ${end} (reprise ${returnDate}, ${countedDays})`;
}

function formatDate(value: string) {
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toFormat("dd/MM/yyyy") : value;
}

function getMonthRange(value: string) {
  const parsed = DateTime.fromFormat(value, "yyyy-MM");
  if (!parsed.isValid) {
    return null;
  }

  return {
    start: parsed.startOf("month"),
    end: parsed.endOf("month"),
    endExclusive: parsed.plus({ months: 1 }).startOf("month"),
  };
}

export default function PayrollTestPage() {
  const user = useUser();
  const utils = trpc.useUtils();
  const { data: secretaryContracts } =
    trpc.user.getSecretaryContractsForDoctor.useQuery(undefined, {
      enabled: user?.tag === "doctor",
    });

  const currentMonth = DateTime.now().toFormat("yyyy-MM");
  const [grossSalary, setGrossSalary] = useState("2500");
  const [secretaryId, setSecretaryId] = useState<string | null>(null);
  const [isCustomGrossSalaryEnabled, setIsCustomGrossSalaryEnabled] =
    useState(false);
  const [periodMonth, setPeriodMonth] = useState(currentMonth);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pendingSave = useRef<{ fingerprint: string; requestId: string } | null>(
    null,
  );
  const savePayslip = trpc.payroll.savePayslip.useMutation({
    onSuccess: async () => {
      await utils.payroll.listSavedPayslips.invalidate();
    },
  });
  const [seniorityBonusDraft, setSeniorityBonusDraft] = useState<string | null>(
    null,
  );
  const [specialLeaveReasons, setSpecialLeaveReasons] = useState<
    Record<string, string>
  >({});

  const activeSecretaryContracts = useMemo(() => {
    return (secretaryContracts || []).filter((contract) => contract.active);
  }, [secretaryContracts]);

  const effectiveSecretaryId = useMemo(() => {
    if (
      secretaryId &&
      activeSecretaryContracts.some((contract) => contract.id === secretaryId)
    ) {
      return secretaryId;
    }

    return activeSecretaryContracts[0]?.id || "";
  }, [activeSecretaryContracts, secretaryId]);

  const selectedSecretaryContract = activeSecretaryContracts.find(
    (contract) => contract.id === effectiveSecretaryId,
  );

  const contractGrossSalary = useMemo(() => {
    if (!selectedSecretaryContract?.salary_amount) {
      return "";
    }

    return selectedSecretaryContract.salary_amount.replace(".", ",");
  }, [selectedSecretaryContract]);

  const displayedGrossSalary = isCustomGrossSalaryEnabled
    ? grossSalary
    : contractGrossSalary;
  const isNetSalaryContract = selectedSecretaryContract?.salary_type === "net";
  const salaryLabel = isCustomGrossSalaryEnabled
    ? isNetSalaryContract
      ? "Salaire net mensuel simulé"
      : "Salaire brut mensuel simulé"
    : selectedSecretaryContract?.salary_type === "net"
      ? "Salaire net mensuel"
      : "Salaire brut mensuel";

  const monthRange = useMemo(() => getMonthRange(periodMonth), [periodMonth]);
  const normalizedGrossSalary = isCustomGrossSalaryEnabled
    ? normalizeDecimal(grossSalary)
    : undefined;

  const monthRdvs = trpc.rdv.getByDateWindow.useQuery(
    {
      startDate: monthRange?.start.toJSDate() || new Date(),
      endDate: monthRange?.endExclusive.toJSDate() || new Date(),
    },
    {
      enabled: user?.tag === "doctor" && !!monthRange,
      retry: false,
    },
  );

  const workedDays = useMemo(() => {
    const workedDates = new Set(
      (monthRdvs.data || [])
        .filter((rdv) => isRealApt(rdv))
        .map((rdv) => DateTime.fromJSDate(rdv.date).toISODate())
        .filter((value): value is string => value !== null),
    );
    return workedDates.size;
  }, [monthRdvs.data]);

  const workedDateList = useMemo(() => {
    return Array.from(
      new Set(
        (monthRdvs.data || [])
          .filter((rdv) => isRealApt(rdv))
          .map((rdv) => DateTime.fromJSDate(rdv.date).toISODate())
          .filter((value): value is string => value !== null),
      ),
    )
      .sort((a, b) => a.localeCompare(b))
      .map((value) => formatDate(value));
  }, [monthRdvs.data]);

  const payrollSimulation = trpc.payroll.simulatePayslip.useQuery(
    {
      secretaryId: effectiveSecretaryId || undefined,
      grossSalary: normalizedGrossSalary,
      referenceDate: monthRange?.end.toISODate() || undefined,
    },
    {
      enabled:
        user?.tag === "doctor" &&
        !!monthRange &&
        effectiveSecretaryId !== "" &&
        (!isCustomGrossSalaryEnabled ||
          parsePositiveDecimal(grossSalary) !== null),
      retry: false,
    },
  );
  const saveSpecialLeaveReason = trpc.staffAbsence.upsert.useMutation({
    onSuccess: async () => {
      await utils.payroll.simulatePayslip.invalidate();
    },
  });
  const setSeniorityBonusOverride =
    trpc.payroll.setSeniorityBonusOverride.useMutation();
  const resetSeniorityBonusOverride =
    trpc.payroll.resetSeniorityBonusOverride.useMutation();
  const ticketUnitValue =
    parsePositiveDecimal(
      selectedSecretaryContract?.ticket_restaurant_amount || "",
    ) || 0;
  const employerPercent =
    parsePercent(
      selectedSecretaryContract?.ticket_restaurant_employer_part || "",
    ) || 0;
  const employeePercent = Math.max(0, 100 - employerPercent);
  const netSocialSalary = Number(payrollSimulation.data?.netSocialSalary || 0);
  const totalEmployerPaid = Number(
    payrollSimulation.data?.totalEmployerPaid || 0,
  );
  const employerGlobalCost = Number(
    payrollSimulation.data?.employerGlobalCost || 0,
  );
  const paidLeaveDays = payrollSimulation.data?.paidLeaveDays || 0;
  const paidLeaveRanges = payrollSimulation.data?.paidLeaveRanges || [];
  const specialLeaveDays = payrollSimulation.data?.specialLeaveDays || 0;
  const specialLeaveAbsences =
    payrollSimulation.data?.specialLeaveAbsences || [];
  const specialLeaveDaysOnWorkedDays =
    payrollSimulation.data?.specialLeaveDaysOnWorkedDays || 0;
  const salaryTax = payrollSimulation.data?.salaryTax;
  const unpaidLeaveDays = payrollSimulation.data?.unpaidLeaveDays || 0;
  const unpaidLeaveHours = payrollSimulation.data?.unpaidLeaveHours || "0.00";
  const theoreticalMonthlyHours =
    payrollSimulation.data?.theoreticalMonthlyHours;
  const deductedMonthlyHours =
    payrollSimulation.data?.deductedMonthlyHours || unpaidLeaveHours;
  const workedMonthlyHours = payrollSimulation.data?.workedMonthlyHours;
  const salaryDeductionRate =
    payrollSimulation.data?.salaryDeductionRate || "0.0000";
  const grossSalaryBeforeSeniorityBonus = Number(
    payrollSimulation.data?.grossSalaryBeforeSeniorityBonus || 0,
  );
  const payableWorkedDays = Math.max(
    0,
    Number(
      (workedDays - unpaidLeaveDays - specialLeaveDaysOnWorkedDays).toFixed(1),
    ),
  );
  const mealVoucher = payrollSimulation.data?.mealVoucher;
  const ticketCount = mealVoucher?.ticketCount || Math.ceil(payableWorkedDays);
  const totalTicketsAmount = Number(mealVoucher?.totalAmount || 0);
  const employeeTicketShareAmount = Number(mealVoucher?.employeeAmount || 0);
  const employerTicketShareAmount = Number(mealVoucher?.employerAmount || 0);
  const netToPay = Number(payrollSimulation.data?.netToPay || 0);
  const isNetSeniorityBonus =
    payrollSimulation.data?.seniorityBonus.isNet ?? isNetSalaryContract;
  const isSavingSeniorityBonus =
    setSeniorityBonusOverride.isPending ||
    resetSeniorityBonusOverride.isPending;
  const calculatedSeniorityBonusAmount =
    payrollSimulation.data?.seniorityBonus.calculatedAmount || "";
  const appliedSeniorityBonusAmount =
    payrollSimulation.data?.seniorityBonus.amount || "";
  const displayedSeniorityBonusAmount =
    seniorityBonusDraft ??
    (appliedSeniorityBonusAmount
      ? formatDecimal(appliedSeniorityBonusAmount)
      : "");

  const canSave =
    !savePayslip.isPending &&
    !isSavingSeniorityBonus &&
    !saveSpecialLeaveReason.isPending &&
    !!monthRange &&
    effectiveSecretaryId !== "" &&
    (!isCustomGrossSalaryEnabled ||
      parsePositiveDecimal(grossSalary) !== null) &&
    !monthRdvs.isLoading &&
    !monthRdvs.error &&
    !payrollSimulation.isLoading &&
    !payrollSimulation.error;

  const handleSave = async () => {
    if (!monthRange) {
      setErrorMessage("Choisissez une période valide.");
      return;
    }

    if (!effectiveSecretaryId) {
      setErrorMessage("Choisissez un contrat secrétaire actif.");
      return;
    }

    if (
      isCustomGrossSalaryEnabled &&
      parsePositiveDecimal(grossSalary) === null
    ) {
      setErrorMessage("Entrez un salaire brut simulé valide.");
      return;
    }

    setErrorMessage(null);

    try {
      if (
        seniorityBonusDraft !== null &&
        parseNonNegativeDecimal(seniorityBonusDraft) !==
          parseNonNegativeDecimal(appliedSeniorityBonusAmount)
      )
        throw new Error(
          "Enregistrez d’abord la modification de la prime d’ancienneté.",
        );
      const values = {
        secretaryId: effectiveSecretaryId,
        referenceDate: monthRange.end.toISODate() || "",
        grossSalary: isCustomGrossSalaryEnabled
          ? Number(normalizeDecimal(grossSalary)).toFixed(2)
          : undefined,
        specialLeaveReasons: getSpecialLeaveReasonsForDownload(
          specialLeaveAbsences,
          specialLeaveReasons,
        ),
      };
      const fingerprint = JSON.stringify(values);
      if (pendingSave.current?.fingerprint !== fingerprint)
        pendingSave.current = { fingerprint, requestId: crypto.randomUUID() };
      const payroll = await savePayslip.mutateAsync({
        ...values,
        requestId: pendingSave.current.requestId,
      });
      pendingSave.current = null;
      toast.success(
        `Fiche enregistrée — version ${payroll.revision}. Disponible dans T Extension.`,
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "Impossible d’enregistrer la fiche de paie.",
      );
    }
  };

  const handleSaveSeniorityBonus = async () => {
    if (!monthRange || !effectiveSecretaryId) {
      setErrorMessage(
        "Choisissez un contrat secrétaire et une période valide.",
      );
      return;
    }

    if (parseNonNegativeDecimal(displayedSeniorityBonusAmount) === null) {
      setErrorMessage("Entrez une prime valide, positive ou nulle.");
      return;
    }

    try {
      await setSeniorityBonusOverride.mutateAsync({
        secretaryId: effectiveSecretaryId,
        referenceDate: monthRange.end.toISODate() || undefined,
        amount: normalizeDecimal(displayedSeniorityBonusAmount),
      });
      await utils.payroll.simulatePayslip.invalidate();
      setSeniorityBonusDraft(null);
      setErrorMessage(null);
      toast.success("Prime d'ancienneté enregistrée.");
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Impossible d'enregistrer la prime d'ancienneté.",
      );
    }
  };

  const handleResetSeniorityBonus = async () => {
    if (!monthRange || !effectiveSecretaryId) {
      return;
    }

    try {
      await resetSeniorityBonusOverride.mutateAsync({
        secretaryId: effectiveSecretaryId,
        referenceDate: monthRange.end.toISODate() || undefined,
      });
      await utils.payroll.simulatePayslip.invalidate();
      setSeniorityBonusDraft(null);
      setErrorMessage(null);
      toast.success("Prime d'ancienneté réinitialisée.");
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Impossible de réinitialiser la prime d'ancienneté.",
      );
    }
  };

  const handleSaveSpecialLeaveReason = async (absence: {
    id: string;
    absenceDate: string;
    partOfDay: "full_day" | "morning" | "afternoon";
  }) => {
    const reason = specialLeaveReasons[absence.id];
    if (reason === undefined || !effectiveSecretaryId) {
      return;
    }

    try {
      await saveSpecialLeaveReason.mutateAsync({
        staffId: effectiveSecretaryId,
        absenceDate: absence.absenceDate,
        partOfDay: absence.partOfDay,
        leaveType: "special",
        reason,
      });
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Impossible d'enregistrer le motif du congé spécial.",
      );
    }
  };

  return (
    <>
      <div className="min-h-full bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.14),transparent_24%),linear-gradient(180deg,#f8fafc_0%,#e2e8f0_100%)] p-7 max-md:p-[18px]">
        <div className="mx-auto max-w-6xl rounded-[28px] bg-white/90 p-7 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
          <div className="mb-6 flex items-end justify-between gap-4 max-md:flex-col max-md:items-start">
            <div>
              <h1 className="m-0 text-[clamp(2rem,3vw,2.8rem)] leading-none text-slate-900">
                Création d&apos;une fiche de paie
              </h1>
              <div className="mt-2.5 max-w-3xl text-base text-slate-600">
                Choisissez la période et enregistrez une version de la fiche à
                partir du contrat, des absences et des tickets restaurant. Son
                PDF reste téléchargeable à tout moment depuis l’historique.
              </div>
            </div>

            {selectedSecretaryContract ? (
              <div className="rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                Tickets restaurant du contrat : {formatAmount(ticketUnitValue)}{" "}
                / ticket, prise en charge employeur {employerPercent.toFixed(0)}
                %
              </div>
            ) : null}
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
            <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-5">
              <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                {user?.tag === "doctor" ? (
                  <label className="block">
                    <div className="mb-2 text-sm font-semibold text-slate-900">
                      Contrat secrétaire existant
                    </div>
                    <select
                      className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base font-normal"
                      value={effectiveSecretaryId}
                      onChange={(event) => {
                        setSecretaryId(event.target.value);
                        setIsCustomGrossSalaryEnabled(false);
                        setSeniorityBonusDraft(null);
                        setErrorMessage(null);
                      }}
                    >
                      {activeSecretaryContracts.map((contract) => (
                        <option key={contract.id} value={contract.id}>
                          {contract.fullname}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                <label className="block">
                  <div className="mb-2 text-sm font-semibold text-slate-900">
                    Période
                  </div>
                  <input
                    type="month"
                    className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base"
                    value={periodMonth}
                    onChange={(event) => {
                      setPeriodMonth(event.target.value);
                      setSeniorityBonusDraft(null);
                      setErrorMessage(null);
                    }}
                  />
                </label>

                <label className="block">
                  <div className="mb-2 text-sm font-semibold text-slate-900">
                    {salaryLabel}
                  </div>
                  <input
                    className="w-full rounded-[14px] border border-slate-300 bg-white px-[14px] py-3 text-base disabled:bg-slate-100 disabled:text-slate-500"
                    value={displayedGrossSalary}
                    onChange={(event) => {
                      setGrossSalary(sanitizeDecimalInput(event.target.value));
                      setErrorMessage(null);
                    }}
                    placeholder="2500"
                    disabled={!isCustomGrossSalaryEnabled}
                  />
                </label>

                <label className="flex items-center gap-3 rounded-[14px] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={isCustomGrossSalaryEnabled}
                    onChange={(event) => {
                      setIsCustomGrossSalaryEnabled(event.target.checked);
                      setErrorMessage(null);
                    }}
                  />
                  {isNetSalaryContract
                    ? "Simuler un salaire mensuel net différent"
                    : "Simuler un salaire mensuel brut différent"}
                </label>

                {specialLeaveAbsences.length > 0 ? (
                  <div className="col-span-2 rounded-[18px] border border-violet-200 bg-violet-50 px-4 py-4 max-md:col-span-1">
                    <div className="text-sm font-semibold text-violet-950">
                      Motifs des congés spéciaux
                    </div>
                    <div className="mt-1 text-xs text-violet-800">
                      Facultatifs, ils sont enregistrés à la sortie du champ et
                      repris dans l&apos;annexe du bulletin.
                    </div>
                    <div className="mt-3 space-y-3">
                      {specialLeaveAbsences.map((absence) => {
                        const partOfDay =
                          absence.partOfDay === "full_day"
                            ? "Journée"
                            : absence.partOfDay === "morning"
                              ? "Matin"
                              : "Après-midi";

                        return (
                          <label key={absence.id} className="block">
                            <div className="mb-1 text-xs font-medium text-violet-950">
                              {formatDate(absence.absenceDate)} - {partOfDay} -{" "}
                              {formatSpecialLeaveDayCount(absence.countedDays)}
                            </div>
                            <input
                              className="w-full rounded-[12px] border border-violet-200 bg-white px-3 py-2 text-sm text-slate-900"
                              value={
                                specialLeaveReasons[absence.id] ??
                                absence.reason ??
                                ""
                              }
                              maxLength={200}
                              onFocus={() => {
                                setSpecialLeaveReasons((current) =>
                                  current[absence.id] === undefined
                                    ? {
                                        ...current,
                                        [absence.id]: absence.reason || "",
                                      }
                                    : current,
                                );
                              }}
                              placeholder="Motif facultatif"
                              onChange={(event) => {
                                setSpecialLeaveReasons((current) => ({
                                  ...current,
                                  [absence.id]: event.target.value,
                                }));
                                setErrorMessage(null);
                              }}
                              onBlur={() => {
                                void handleSaveSpecialLeaveReason(absence);
                              }}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <div className="rounded-[18px] border border-blue-100 bg-blue-50 px-4 py-4">
                  <div className="text-sm font-semibold text-blue-950">
                    Jours travaillés
                  </div>
                  <div className="mt-2 text-3xl font-semibold text-blue-900">
                    {monthRdvs.isLoading ? "..." : payableWorkedDays}
                  </div>
                  <div className="mt-2 text-xs text-blue-700">
                    Calcul automatique sur les jours du{" "}
                    {monthRange
                      ? formatDate(monthRange.start.toISODate() || "")
                      : "-"}{" "}
                    au{" "}
                    {monthRange
                      ? formatDate(monthRange.end.toISODate() || "")
                      : "-"}{" "}
                    avec au moins un rdv normal dans l&apos;agenda Cadendar,
                    puis retrait des jours sans solde et des congés spéciaux.
                  </div>
                  {unpaidLeaveDays > 0 || specialLeaveDaysOnWorkedDays > 0 ? (
                    <div className="mt-2 text-xs text-blue-800">
                      Base agenda {formatDayCount(workedDays)} moins absences
                      sans solde {formatDayCount(unpaidLeaveDays)} et congés
                      spéciaux{" "}
                      {formatSpecialLeaveDayCount(specialLeaveDaysOnWorkedDays)}
                      .
                    </div>
                  ) : null}
                  {!monthRdvs.isLoading && workedDateList.length > 0 ? (
                    <div className="mt-3 rounded-[14px] bg-white/80 px-3 py-3 text-xs text-blue-950">
                      <div className="font-semibold">Dates retenues</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {workedDateList.map((date) => (
                          <span
                            key={date}
                            className="rounded-full border border-blue-200 bg-white px-2.5 py-1"
                          >
                            {date}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {selectedSecretaryContract ? (
                <div className="mt-4 text-xs text-slate-500">
                  {!isCustomGrossSalaryEnabled
                    ? `Le salaire ${isNetSalaryContract ? "net" : "brut"} mensuel est verrouillé et repris depuis le contrat sélectionné. Activez la simulation si vous voulez tester un autre salaire.`
                    : `La date d'embauche, la prime d'ancienneté et les tickets restaurant restent repris du contrat sélectionné. Seul le salaire ${isNetSalaryContract ? "net" : "brut"} mensuel est simulé.`}
                </div>
              ) : null}

              {payrollSimulation.error ? (
                <div className="mt-4 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {payrollSimulation.error.message}
                </div>
              ) : null}

              {monthRdvs.error ? (
                <div className="mt-4 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {monthRdvs.error.message}
                </div>
              ) : null}

              {errorMessage ? (
                <div className="mt-4 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {errorMessage}
                </div>
              ) : null}
            </div>

            <div className="rounded-[22px] border border-slate-200 bg-slate-950 p-5 text-white shadow-[0_20px_60px_rgba(15,23,42,0.16)]">
              <div className="text-sm uppercase tracking-[0.22em] text-slate-400">
                Synthèse
              </div>

              <div className="mt-5 grid gap-3">
                <div className="flex items-center justify-between gap-4 rounded-[16px] bg-white/5 px-4 py-3">
                  <span className="text-slate-300">
                    Salaire brut hors prime
                  </span>
                  <span className="font-semibold text-white">
                    {formatAmount(grossSalaryBeforeSeniorityBonus)}
                  </span>
                </div>

                <div className="rounded-[16px] bg-white/5 px-4 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-white">
                        Prime mensuelle personnalisée
                      </div>
                      <div className="mt-1 text-xs text-slate-400">
                        Montants {isNetSeniorityBonus ? "nets" : "bruts"}.
                      </div>
                    </div>
                    {payrollSimulation.data?.seniorityBonus.hasOverride ? (
                      <button
                        type="button"
                        className="rounded-lg border border-slate-500 px-3 py-2 text-xs font-semibold text-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={handleResetSeniorityBonus}
                        disabled={isSavingSeniorityBonus}
                      >
                        Réinitialiser
                      </button>
                    ) : null}
                  </div>
                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <label className="min-w-[120px] flex-1">
                      <span className="mb-1 block text-xs font-medium text-slate-300">
                        Calculée
                      </span>
                      <input
                        className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200"
                        value={
                          calculatedSeniorityBonusAmount
                            ? formatDecimal(calculatedSeniorityBonusAmount)
                            : ""
                        }
                        readOnly
                      />
                    </label>
                    <label className="min-w-[120px] flex-1">
                      <span className="mb-1 block text-xs font-medium text-slate-300">
                        Personnalisée
                      </span>
                      <input
                        inputMode="decimal"
                        className="w-full rounded-lg border border-slate-500 bg-white px-3 py-2 text-sm text-slate-950"
                        value={displayedSeniorityBonusAmount}
                        onChange={(event) => {
                          setSeniorityBonusDraft(
                            sanitizeDecimalInput(event.target.value),
                          );
                          setErrorMessage(null);
                        }}
                        disabled={isSavingSeniorityBonus}
                      />
                    </label>
                    <button
                      type="button"
                      className="rounded-lg bg-blue-400 px-3 py-2 text-sm font-semibold text-slate-950 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={handleSaveSeniorityBonus}
                      disabled={
                        isSavingSeniorityBonus ||
                        displayedSeniorityBonusAmount === ""
                      }
                    >
                      {setSeniorityBonusOverride.isPending
                        ? "Enregistrement..."
                        : "Enregistrer"}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-[16px] bg-white/5 px-4 py-3">
                  <span className="text-slate-300">Salaire brut</span>
                  <span className="font-semibold text-white">
                    {formatAmount(
                      Number(payrollSimulation.data?.grossSalary || 0),
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-[16px] bg-white/5 px-4 py-3">
                  <span className="text-slate-300">
                    Prime d&apos;ancienneté{" "}
                    {isNetSeniorityBonus ? "nette" : "brute"}
                  </span>
                  <span className="font-semibold text-white">
                    {formatAmount(
                      Number(
                        payrollSimulation.data?.seniorityBonus.amount || 0,
                      ),
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-[16px] bg-white/5 px-4 py-3">
                  <span className="text-slate-300">Salaire net social</span>
                  <span className="font-semibold text-white">
                    {formatAmount(netSocialSalary)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-[16px] bg-white/5 px-4 py-3">
                  <span className="text-slate-300">
                    Total versé par l&apos;employeur
                  </span>
                  <span className="font-semibold text-white">
                    {formatAmount(totalEmployerPaid)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-[16px] bg-white/5 px-4 py-3">
                  <span className="text-slate-300">Coût global employeur</span>
                  <span className="font-semibold text-white">
                    {formatAmount(employerGlobalCost)}
                  </span>
                </div>

                {salaryTax ? (
                  <div className="rounded-[16px] bg-fuchsia-400/10 px-4 py-4 text-sm text-fuchsia-50 ring-1 ring-fuchsia-300/20">
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-semibold text-white">
                        Taxe sur les salaires
                      </span>
                      <span className="font-semibold text-white">
                        {formatAmount(Number(salaryTax.amount || 0))}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-fuchsia-100">
                      Barème métropole {salaryTax.year}, appliqué sur une
                      assiette de{" "}
                      {formatAmount(Number(salaryTax.taxableBase || 0))}.
                    </div>
                    <div className="mt-3 overflow-hidden rounded-[14px] border border-fuchsia-200/20 bg-slate-950/30">
                      <table className="w-full border-collapse text-left text-xs text-fuchsia-50">
                        <thead>
                          <tr className="bg-white/5 text-fuchsia-100">
                            <th className="px-3 py-2 font-semibold">Tranche</th>
                            <th className="px-3 py-2 font-semibold">Taux</th>
                            <th className="px-3 py-2 font-semibold">Base</th>
                            <th className="px-3 py-2 font-semibold">Montant</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salaryTax.brackets.map((bracket, index) => {
                            const lowerBound = Number(bracket.lowerBound);
                            const upperBound =
                              bracket.upperBound === null
                                ? null
                                : Number(bracket.upperBound);
                            const trancheLabel =
                              upperBound === null
                                ? `Au-delà de ${formatAmount(lowerBound)}`
                                : lowerBound === 0
                                  ? `Jusqu'à ${formatAmount(upperBound)}`
                                  : `De ${formatAmount(lowerBound)} à ${formatAmount(upperBound)}`;

                            return (
                              <tr
                                key={`${bracket.lowerBound}:${bracket.upperBound}:${index}`}
                                className="border-t border-white/10"
                              >
                                <td className="px-3 py-2">{trancheLabel}</td>
                                <td className="px-3 py-2">
                                  {formatRate(bracket.rate)}
                                </td>
                                <td className="px-3 py-2">
                                  {formatAmount(
                                    Number(bracket.taxableBase || 0),
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {formatAmount(Number(bracket.amount || 0))}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 text-xs text-fuchsia-100">
                      Le montant total est arrondi à l&apos;euro le plus proche
                      pour la taxe due.
                    </div>
                  </div>
                ) : null}

                <div className="flex items-center justify-between gap-4 rounded-[16px] bg-white/5 px-4 py-3">
                  <span className="text-slate-300">Congés payés saisis</span>
                  <span className="font-semibold text-white">
                    {formatDayCount(paidLeaveDays)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-[16px] bg-white/5 px-4 py-3">
                  <span className="text-slate-300">
                    Congés spéciaux rémunérés
                  </span>
                  <span className="font-semibold text-white">
                    {formatSpecialLeaveDayCount(specialLeaveDays)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-[16px] bg-white/5 px-4 py-3">
                  <span className="text-slate-300">Absences sans solde</span>
                  <span className="font-semibold text-white">
                    {formatDayCount(unpaidLeaveDays)}
                  </span>
                </div>

                <div className="rounded-[16px] bg-amber-400/10 px-4 py-3 text-sm text-amber-50 ring-1 ring-amber-300/20">
                  <div className="font-semibold text-white">
                    Retenue sans solde
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-4">
                    <span className="text-amber-100">
                      Heures mensualisées théoriques
                    </span>
                    <span>
                      {theoreticalMonthlyHours
                        ? formatHourCount(theoreticalMonthlyHours)
                        : "-"}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-4">
                    <span className="text-amber-100">Heures travaillées</span>
                    <span>
                      {workedMonthlyHours
                        ? formatHourCount(workedMonthlyHours)
                        : "-"}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-4">
                    <span className="text-amber-100">
                      Heures non travaillées
                    </span>
                    <span>{formatHourCount(deductedMonthlyHours)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-4 font-semibold text-white">
                    <span>Retenue appliquée au salaire</span>
                    <span>{formatPercentFromRatio(salaryDeductionRate)}</span>
                  </div>
                </div>

                {paidLeaveRanges.length > 0 ? (
                  <div className="rounded-[16px] bg-white/5 px-4 py-3 text-sm text-slate-200">
                    <div className="font-semibold text-white">
                      Plages de congés
                    </div>
                    <div className="mt-2 flex flex-col gap-2">
                      {paidLeaveRanges.map((range, index) => (
                        <span
                          key={`${range.startDate}:${range.returnDate}:${index}`}
                        >
                          {monthRange
                            ? formatPaidLeaveRange({
                                range,
                                periodDate: monthRange.end,
                              })
                            : formatPaidLeaveRange({
                                range,
                                periodDate: DateTime.fromISO(range.endDate),
                              })}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="rounded-[18px] bg-emerald-400/10 px-4 py-4 ring-1 ring-emerald-300/20">
                  <div className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-emerald-100">Tickets restaurant</span>
                    <span className="font-semibold text-white">
                      {ticketCount} x {formatAmount(ticketUnitValue)}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-emerald-100">
                    Base de calcul : {formatDayCount(payableWorkedDays)},
                    arrondie à l&apos;entier supérieur pour les tickets
                    restaurant.
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-4 text-sm text-emerald-100">
                    <span>Part employeur ({employerPercent.toFixed(0)}%)</span>
                    <span>{formatAmount(employerTicketShareAmount)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-4 text-sm text-emerald-100">
                    <span>
                      Retenue sur salaire ({employeePercent.toFixed(0)}%)
                    </span>
                    <span>{formatAmount(employeeTicketShareAmount)}</span>
                  </div>
                </div>

                <div className="mt-2 rounded-[18px] bg-white px-4 py-4 text-slate-950">
                  <div className="text-sm font-semibold text-slate-500">
                    Net à payer
                  </div>
                  <div className="mt-2 text-3xl font-semibold text-slate-950">
                    {formatAmount(netToPay)}
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    Net social {formatAmount(netSocialSalary)} moins retenue sur
                    salaire tickets restaurant{" "}
                    {formatAmount(employeeTicketShareAmount)}.
                  </div>
                </div>

                <button
                  type="button"
                  className="mt-3 rounded-[14px] border-none bg-blue-600 px-[18px] py-3 font-bold text-white disabled:cursor-default disabled:bg-slate-600"
                  onClick={() => void handleSave()}
                  disabled={!canSave}
                >
                  {savePayslip.isPending
                    ? "Enregistrement…"
                    : "Enregistrer la fiche de paie"}
                </button>
                <p className="mt-2 text-xs text-slate-500">
                  L’enregistrement fige cette version, indépendamment du PDF.
                  Une correction crée une nouvelle version. Aucun envoi au TESE
                  n’est automatique.
                </p>
              </div>
            </div>
          </div>
          <SavedPayslipsPanel enabled={user?.tag === "doctor"} />
        </div>
      </div>
    </>
  );
}
